
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";
import { toast } from "@/hooks/use-toast";

export interface ScriptureResponse {
  id: string;
  reference: string;
  text: string;
  context?: string;
  commentary?: string;
  guidance?: string;
}

export const useFindScripture = () => {
  const [prompt, setPrompt] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [scriptures, setScriptures] = useState<ScriptureResponse[]>([]);
  const [loadingStates, setLoadingStates] = useState<Record<string, { commentary: boolean; guidance: boolean }>>({});

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!prompt) return;
    
    try {
      setIsLoading(true);
      setScriptures([]);
      
      const { data, error } = await supabase.functions.invoke('find-your-verse', {
        body: {
          action: 'findVerses',
          situation: prompt
        }
      });

      if (error) {
        console.error("Error invoking find-your-verse function:", error);
        toast({
          title: "Error",
          description: "Failed to find verses. Please try again later.",
          variant: "destructive",
        });
        return;
      }

      if (data && Array.isArray(data.verses)) {
        const transformedVerses = data.verses.map((verse: any) => ({
          id: uuidv4(),
          reference: verse.reference,
          text: verse.text,
          context: verse.explanation
        }));
        console.log("Setting scriptures:", transformedVerses);
        setScriptures(transformedVerses);
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getCommentary = async (id: string) => {
    const scripture = scriptures.find(s => s.id === id);
    if (!scripture) return;
    
    setLoadingStates(prev => ({
      ...prev,
      [id]: { ...prev[id], commentary: true }
    }));
    
    try {
      const { data, error } = await supabase.functions.invoke('find-your-verse', {
        body: {
          action: 'getCommentary',
          reference: scripture.reference
        }
      });
      
      if (error) throw error;
      
      setScriptures(prev => 
        prev.map(s => s.id === id ? { ...s, commentary: data.commentary } : s)
      );
    } catch (error) {
      console.error("Error getting commentary:", error);
      toast({
        title: "Error",
        description: "Failed to get commentary. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingStates(prev => ({
        ...prev,
        [id]: { ...prev[id], commentary: false }
      }));
    }
  };

  const getGuidance = async (id: string) => {
    const scripture = scriptures.find(s => s.id === id);
    if (!scripture) return;
    
    setLoadingStates(prev => ({
      ...prev,
      [id]: { ...prev[id], guidance: true }
    }));
    
    try {
      const { data, error } = await supabase.functions.invoke('find-your-verse', {
        body: {
          action: 'getGuidance',
          situation: prompt,
          reference: scripture.reference
        }
      });
      
      if (error) throw error;
      
      setScriptures(prev => 
        prev.map(s => s.id === id ? { ...s, guidance: data.guidance } : s)
      );
    } catch (error) {
      console.error("Error getting guidance:", error);
      toast({
        title: "Error",
        description: "Failed to get personal guidance. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingStates(prev => ({
        ...prev,
        [id]: { ...prev[id], guidance: false }
      }));
    }
  };

  const saveScriptures = async () => {
    console.log("saveScriptures called");
    console.log("Current scriptures:", scriptures);
    console.log("Current prompt:", prompt);
    
    if (!scriptures.length || !prompt) {
      console.warn("No scriptures or prompt to save");
      toast({
        title: "No Scriptures",
        description: "There are no scriptures to save.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      console.log("Attempting to save scriptures...");
      
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error("Session error:", sessionError);
        throw sessionError;
      }
      
      if (!sessionData.session) {
        console.log("No active session found");
        toast({
          title: "Authentication Required",
          description: "Please sign in to save scriptures.",
          variant: "destructive",
        });
        return;
      }

      console.log("User is authenticated, proceeding with save");
      console.log("Session user:", sessionData.session.user.id);
      
      // Create a safe copy of scriptures for JSON serialization
      const scripturesCopy = JSON.parse(JSON.stringify(scriptures));
      
      // Create a content object for storage
      const contentObj = {
        prompt,
        scriptures: scripturesCopy,
        createdAt: new Date().toISOString()
      };

      console.log("Saving content:", contentObj);
      console.log("Saving to Supabase...");
      
      const { error: insertError } = await supabase
        .from('saved_responses')
        .insert({
          tool_type: 'find_your_verse',
          title: `Find Your Verse: ${prompt.substring(0, 50)}${prompt.length > 50 ? '...' : ''}`,
          content: contentObj,
          user_id: sessionData.session.user.id
        });

      if (insertError) {
        console.error('Insert error:', insertError);
        throw insertError;
      }

      console.log("Save successful");
      toast({
        title: "Scriptures Saved",
        description: "Your scriptures have been saved to your dashboard.",
      });
    } catch (error) {
      console.error('Error saving scriptures:', error);
      toast({
        title: "Error",
        description: "Failed to save scriptures. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const saveItem = async (type: 'verse' | 'commentary' | 'guidance', scripture: ScriptureResponse) => {
    console.log(`Saving individual ${type} for ${scripture.reference}`);
    
    try {
      setIsSaving(true);
      
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw sessionError;
      }
      
      if (!sessionData.session) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to save scriptures.",
          variant: "destructive",
        });
        return;
      }

      // Prepare content based on item type with all necessary data
      let content: Record<string, any> = {};
      let title = '';
      
      switch (type) {
        case 'verse':
          content = {
            reference: scripture.reference,
            text: scripture.text,
            context: scripture.context,
            prompt: prompt, // Include the original prompt
            createdAt: new Date().toISOString()
          };
          title = `Verse: ${scripture.reference}`;
          break;
        
        case 'commentary':
          content = {
            reference: scripture.reference,
            text: scripture.text,
            commentary: scripture.commentary,
            prompt: prompt, // Include the original prompt
            createdAt: new Date().toISOString()
          };
          title = `Commentary: ${scripture.reference}`;
          break;

        case 'guidance':
          content = {
            reference: scripture.reference,
            text: scripture.text,
            guidance: scripture.guidance,
            prompt: prompt, // Include the original prompt
            createdAt: new Date().toISOString()
          };
          title = `Guidance: ${scripture.reference}`;
          break;
      }
      
      // Log the content being saved for debugging
      console.log(`Saving ${type} content:`, content);
      
      const { error: insertError } = await supabase
        .from('saved_responses')
        .insert({
          tool_type: 'find_your_verse',
          title,
          content,
          user_id: sessionData.session.user.id
        });

      if (insertError) {
        throw insertError;
      }

      toast({
        title: "Saved Successfully",
        description: `${type.charAt(0).toUpperCase() + type.slice(1)} for ${scripture.reference} has been saved to your dashboard.`,
      });
    } catch (error) {
      console.error(`Error saving ${type}:`, error);
      toast({
        title: "Error",
        description: `Failed to save ${type}. Please try again later.`,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Share content methods
  const shareContent = (scripture: ScriptureResponse, type: 'verse' | 'commentary' | 'guidance') => {
    let textToShare = '';
    let title = '';
    
    switch (type) {
      case 'verse':
        title = `Scripture: ${scripture.reference}`;
        textToShare = `${scripture.reference}: "${scripture.text}"`;
        if (scripture.context) {
          textToShare += `\n\nContext: ${scripture.context}`;
        }
        break;
      
      case 'commentary':
        title = `Commentary on ${scripture.reference}`;
        textToShare = `Commentary on ${scripture.reference}:\n\n"${scripture.text}"\n\n${scripture.commentary}`;
        break;

      case 'guidance':
        title = `Personal Guidance from ${scripture.reference}`;
        textToShare = `Personal Guidance from ${scripture.reference}:\n\n"${scripture.text}"\n\n${scripture.guidance}`;
        break;
    }
    
    textToShare += '\n\n~ Shared from Selah App';
    
    return {
      title,
      text: textToShare,
      url: window.location.href,
    };
  };

  const shareViaEmail = (scripture: ScriptureResponse, type: 'verse' | 'commentary' | 'guidance') => {
    const { title, text } = shareContent(scripture, type);
    const subject = encodeURIComponent(title);
    const body = encodeURIComponent(text);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    
    toast({
      title: "Email Client Opened",
      description: "Your email client should be opening to share this content.",
    });
  };

  const shareViaSMS = (scripture: ScriptureResponse, type: 'verse' | 'commentary' | 'guidance') => {
    const { text } = shareContent(scripture, type);
    const smsText = encodeURIComponent(text);
    
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      window.location.href = `sms:?&body=${smsText}`;
    } else {
      navigator.clipboard.writeText(text).then(() => {
        toast({
          title: "Copied to Clipboard",
          description: "The content has been copied to your clipboard as SMS sharing is only available on mobile devices.",
        });
      });
    }
  };

  const shareToFacebook = (scripture: ScriptureResponse, type: 'verse' | 'commentary' | 'guidance') => {
    const { url } = shareContent(scripture, type);
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(facebookShareUrl, '_blank', 'width=600,height=400');
    
    toast({
      title: "Sharing to Facebook",
      description: "A Facebook share dialog should have opened.",
    });
  };

  return {
    prompt,
    setPrompt,
    isLoading,
    isSaving,
    scriptures,
    loadingStates,
    handleSubmit,
    getCommentary,
    getGuidance,
    saveScriptures,
    saveItem,
    shareViaEmail,
    shareViaSMS,
    shareToFacebook
  };
};
